<template>
  <NavigationBar
    :class="['navigation', { ['navigation--dark']: isDarkTheme }]"
    :id="ID_NAV_BAR"
    :fixed="false"
    :skipToContentText="$t('SKIP_TO_CONTENT')"
    :skipToFooterText="$t('SKIP_TO_FOOTER')"
    @mobile:open="openMobileMenu"
    @mobile:close="closeMobileMenu"
    @skipToContent="skipToMainContent"
    @skipToFooter="skipToFooterContent"
  >
    <template #left>
      <logo :invert="isDarkTheme" />
    </template>

    <template #mobile>
      <ShBlade
        :is-push="true"
        :invert="isDarkTheme"
        :visible.sync="isMobileMenuOpen"
        :canGoBack="hasSubNav"
        :title="navigationTitle"
        @back:click="handleViewChange(VIEWS.MobileNav)"
      >
        <component
          v-bind:is="activeComponent"
          :routes="selectedRoutes"
          @navigation:active="handleViewChange"
        />
      </ShBlade>
    </template>

    <component
      v-for="route in routes"
      :key="route.link.value.text"
      :is="route.component"
      :isActive="route.isActive"
      :title="route.link.value.text"
    >
      <template v-if="route.hasChildren">
        <NavigationItem
          v-for="subRoute in route.items"
          :key="subRoute.link.value.text"
        >
          <ShLink
            v-if="subRoute.link.value.href"
            class="navigation__sublinks"
            :to="subRoute.link.value.href"
            :target="subRoute.link.value.target"
          >
            {{ subRoute.link.value.text }}
          </ShLink>
        </NavigationItem>
      </template>
      <ShLink
        v-else
        class="navigation__link"
        :to="route.link.value.href"
        :target="route.link.value.target"
      >
        {{ route.link.value.text }}
      </ShLink>
    </component>

    <template #right>
      <div v-if="hasProfile">
        <HouseBoardIcon v-if="!isNonMember" />
        <ShLink class="navigation__link navigation__profile-image" :to="profileLink">
          <ProfileImage
            :imageUrl="profile.imageUrl"
            :altText="$t('ALT_MORE_PROFILE_IMAGE')"
            :aria-label="$t('ALT_MORE_PROFILE_IMAGE')"
          >
            <ShIcon
              role="button"
              aria-label="profile-image"
              tabindex="0"
              size="large"
              :name="profileIcon"
            />
          </ProfileImage>
        </ShLink>
        <ProfileImage
          v-if="profile"
          class="navigation__profile-image-mobile"
          :altText="$t('ALT_MORE_PROFILE_IMAGE')"
          :imageUrl="profile.imageUrl"
          :aria-label="$t('ALT_MORE_PROFILE_IMAGE')"
          @click.native="openProfileMenu"
        >
          <ShIcon
            name="human"
            size="large"
          />
        </ProfileImage>
      </div>
      <div v-else class="navigation__public-links">
        <sh-button class="navigation__link-sign-in" naked="true" role="link" @click="signIn">
          {{ $t('NAV_SIGN_IN') }}
        </sh-button>
        <sh-button
          class="navigation__link-membership"
          :size="membershipBtnSize"
          :to="localePath({ path: '/membership' })"
        >
          {{ $t('NAV_MEMBERSHIP') }}
        </sh-button>
      </div>
    </template>
  </NavigationBar>
</template>

<script>
import {
  NavigationBar,
  NavigationItem,
  NavigationDropdown,
  ProfileImage,
  ShIcon,
  ShButton,
  ShBlade
} from '@sohohouse/design-system'
import { createNamespacedHelpers, mapGetters } from 'vuex'

import UIMixin from '~/mixins/ui'
import logo from '~/components/logo'
import HouseBoardIcon from '~/components/house-board-icon'
import { ID_NAV_BAR } from '~/constants/integration'
import { VIEWS } from '~/constants/navbar-views'
import THEME from '~/constants/theme'
import isEmptyObject from '~/helpers/is-empty-object'
import ShLink from '~/components/sh-link'
import get from '~/helpers/get'

import MobileNav from './MobileNav'
import MobileSubNav from './MobileSubNav'
import MobileSubscription from './MobileSubscription'

const { mapState: mapEnvState, mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  components: {
    HouseBoardIcon,
    NavigationBar,
    NavigationItem,
    ProfileImage,
    ShIcon,
    ShButton,
    ShLink,
    NavigationDropdown,
    MobileNav,
    MobileSubNav,
    ShBlade,
    logo,
    MobileSubscription
  },

  props: {
    profile: {
      type: Object,
      default: () => ({})
    }
  },

  mixins: [
    UIMixin
  ],

  data () {
    return {
      ID_NAV_BAR,
      VIEWS,
      activeComponent: VIEWS.MobileNav,
      activeSubRoute: {},
      isMobileMenuOpen: false
    }
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapEnvState(['header']),
    ...mapEnvGetters(['isDarkTheme']),

    routes () {
      return this.header.items.map(item => {
        const hasChildren = item.items && item.items.length > 0
        return {
          ...item,
          hasChildren,
          isActive: this.hasActiveChild(item.items),
          component: hasChildren ? 'navigation-dropdown' : 'navigation-item'
        }
      }) || []
    },

    hasProfile () {
      return this.profile && !isEmptyObject(this.profile)
    },

    profileIcon () {
      return this.isDarkTheme ? 'humanInvert' : 'human'
    },

    membershipBtnSize () {
      return this.$device.isMobile || this.$device.isTablet ? 'small' : 'medium'
    },

    hasSubNav () {
      return this.activeComponent !== VIEWS.MobileNav && this.activeSubRoute !== {}
    },

    navigationTitle () {
      if (this.activeComponent === VIEWS.MobileSubscription) {
        return this.$t('SUBSCRIBE_MOBILE_TITLE')
      }
      return this.hasSubNav ? get(this.activeSubRoute, 'link.value.text', '') : ''
    },

    navigationTheme () {
      return this.isDarkTheme ? THEME.Dark : THEME.Light
    },

    selectedRoutes () {
      return !isEmptyObject(this.activeSubRoute) ? this.activeSubRoute : this.routes
    },

    isNonMember () {
      const { currentUser } = this

      return currentUser && currentUser.membership.nonMemberAccount
    },

    profileLink () {
      return this.localePath(this.isNonMember ? 'settings-preferences' : 'settings-profile')
    }
  },

  methods: {
    skipToMainContent () {
      this.$emit('skipToMainContent')
    },

    skipToFooterContent () {
      this.$emit('skipToFooterContent')
    },

    signIn () {
      this.$login()
    },

    openProfileMenu () {
      this.$ui.modal.open('profile-menu')
    },

    handleViewChange (component = VIEWS.MobileNav, value = {}) {
      this.activeComponent = component
      this.activeSubRoute = value
    },

    openMobileMenu () {
      this.isMobileMenuOpen = true
    },

    closeMobileMenu () {
      this.isMobileMenuOpen = false
    },

    hasActiveChild (items) {
      return items.some(({ link: { value: { href } } }) => {
        const [hasMatch] = this.$route.path.match(href) || []
        return Boolean(hasMatch)
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.navigation {
  width: 100%;
  background-color: $color-white;

  @include tablet {
    position: unset;
  }

  &__home-link {
    text-transform: uppercase;
    @include font('070')
  }

  &__link {
    &.nuxt-link-active {
      @include underline;
    }
  }

  &__sublinks {
    margin-bottom: $space-030;

    &.nuxt-link-active {
      @include underline;
    }
  }

  &__public-links {
    align-items: center;
    justify-items: center;

    > * {
      margin-right: $space-060;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  &__link-sign-in {
    display: none;

    @include desktop {
      display: block;
    }
  }

  &__profile-image {
    display: none;

    @include tablet {
      display: flex;
      cursor: pointer;
    }
  }

  &__profile-image-mobile {
    @include tablet {
      display: none;
    }
  }

  &__profile-icon-desktop {
    display: none;

    @include desktop {
      display: block;
    }
  }

  &__profile-icon-mobile {
    display: block;

    @include desktop {
      display: none;
    }
  }

  &__mobile-item {
    display: block;

    @include tablet {
      display: none;
    }
  }

  &--dark {
    color: $color-white;

    .navigation__home-link {
      color: $color-white;
    }
  }
}
</style>
